import { useState } from "react";
import { X, FileText } from "lucide-react";

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: { content: string; type: string }) => void;
  noteType?: "clinical" | "aftercare" | "additional";
  title?: string;
}

export default function AddNoteModal({
  isOpen,
  onClose,
  onSave,
  noteType = "additional",
  title = "Add Note",
}: AddNoteModalProps) {
  const [content, setContent] = useState("");

  const handleSave = () => {
    if (content.trim()) {
      onSave?.({ content, type: noteType });
      setContent("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-purple-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#2d2d2d]">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Note Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your note here..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 resize-none h-32 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!content.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#5f3d9a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-4 h-4" />
              Save Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}